import React from 'react';
import styled from 'styled-components';
import { Typography, Button } from '../../GlobalStyles';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  text-align: center;
  background-color: ${({ theme }) => theme.background};
`;

const ErrorIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
`;

const ErrorTitle = styled(Typography)`
  margin-bottom: 16px;
`;

const ErrorMessage = styled(Typography)`
  margin-bottom: 32px;
  max-width: 500px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    this.setState({
      error,
      errorInfo,
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // TODO: Send error to logging service (e.g., Sentry, LogRocket)
    // logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle $variant="h2" $font="Bold">
            {this.props.title || 'Something went wrong'}
          </ErrorTitle>
          <ErrorMessage $variant="body1" $color="textSecondary">
            {this.props.message ||
              'An unexpected error occurred. Please try refreshing the page or return to the homepage.'}
          </ErrorMessage>
          <ButtonGroup>
            <Button onClick={this.handleReload} $variant="primary">
              Refresh Page
            </Button>
            <Button onClick={this.handleGoHome} $variant="secondary">
              Go to Homepage
            </Button>
          </ButtonGroup>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '32px', textAlign: 'left', maxWidth: '800px' }}>
              <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                Error Details (Development Only)
              </summary>
              <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
